'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

type SourceTag = 'isx_verified' | 'self_declared' | 'third_party' | 'pending' | 'expired' | 'not_provided';

interface PassportField {
  label: string;
  value: string;
  source: SourceTag;
  verifiedDate?: string;
}

const sourceConfig: Record<SourceTag, { label: string; cls: string; icon: string }> = {
  isx_verified: { label: 'ISX Vérifié', cls: 'bg-positive/10 text-positive border-positive/20', icon: 'CheckBadgeIcon' },
  self_declared: { label: 'Auto-déclaré', cls: 'bg-warning/10 text-warning border-warning/20', icon: 'UserIcon' },
  third_party: { label: 'Tiers vérifié', cls: 'bg-info/10 text-info border-info/20', icon: 'BuildingOffice2Icon' },
  pending: { label: 'En attente', cls: 'bg-muted text-muted-foreground border-border', icon: 'ClockIcon' },
  expired: { label: 'Expiré', cls: 'bg-negative/10 text-negative border-negative/20', icon: 'ExclamationCircleIcon' },
  not_provided: { label: 'Non fourni', cls: 'bg-muted text-muted-foreground/60 border-border/50', icon: 'MinusCircleIcon' },
};

function SourceBadge({ source }: { source: SourceTag }) {
  const cfg = sourceConfig[source];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-700 uppercase tracking-wide px-2 py-0.5 rounded-full border ${cfg.cls}`}>
      <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={10} />
      {cfg.label}
    </span>
  );
}

const passportSections: { title: string; fields: PassportField[] }[] = [
  {
    title: 'Identité & Statut',
    fields: [
      { label: 'Nom professionnel', value: 'Amel Benali', source: 'isx_verified', verifiedDate: '15 Aoû 2026' },
      { label: 'Statut professionnel', value: 'Créateur de contenu indépendant', source: 'self_declared' },
      { label: 'Capacité de facturation', value: 'Oui — Auto-entrepreneur', source: 'self_declared' },
      { label: 'Identité vérifiée', value: 'Vérifiée par ISX', source: 'isx_verified', verifiedDate: '10 Aoû 2026' },
    ]
  },
  {
    title: 'Audience & Plateformes',
    fields: [
      { label: 'Plateforme principale', value: 'Instagram', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Followers Instagram', value: '284 000', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Engagement Rate', value: '8.7%', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Audience principale', value: 'Algérie (78%)', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'YouTube', value: '12 400 abonnés', source: 'isx_verified', verifiedDate: '18 Aoû 2026' },
      { label: 'TikTok', value: 'Non connecté', source: 'pending' },
    ]
  },
  {
    title: 'Scores ISX',
    fields: [
      { label: 'ISX Score Global', value: '87/100', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Professional Reputation Score', value: '82/100', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Brand Score', value: '88/100', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Audience Quality', value: '91/100', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Risk Score', value: '12/100 (Faible)', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
    ]
  },
  {
    title: 'Collaborations & Fiabilité',
    fields: [
      { label: 'Collaborations vérifiées', value: '42', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Note marques/agences', value: '4.6/5', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Livraison dans les délais', value: '96%', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
      { label: 'Respect du brief', value: '94%', source: 'isx_verified', verifiedDate: '19 Aoû 2026' },
    ]
  },
  {
    title: 'Formation & Développement',
    fields: [
      { label: 'Formation Creator-to-CEO', value: '72H — Complétée', source: 'third_party', verifiedDate: '01 Juil 2026' },
      { label: 'Niveau', value: 'Advanced Professional', source: 'third_party' },
      { label: 'Score Capstone', value: '91/100', source: 'third_party' },
      { label: 'Certification fiscale DZ', value: 'Non fournie', source: 'not_provided' },
    ]
  },
];

export default function ProfessionalPassportPage() {
  const [exporting, setExporting] = useState(false);

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => setExporting(false), 2000);
  };

  return (
    <AppLayout currentPath="/professional-passport">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-700 uppercase tracking-widest text-violet-400 bg-violet-400/10 px-2 py-0.5 rounded-full">Professional Passport</span>
              <span className="text-xs text-muted-foreground">Généré le 19 Aoû 2026 · Méthodologie v1.2</span>
            </div>
            <h1 className="text-2xl font-700 text-foreground">Passeport Professionnel ISX</h1>
            <p className="text-sm text-muted-foreground mt-1">Document portable · Chaque donnée indique sa source de vérification</p>
          </div>
          <button onClick={handleExport} disabled={exporting} className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-sm font-700 transition-all disabled:opacity-70">
            {exporting ? (
              <><Icon name="ArrowPathIcon" size={16} className="animate-spin" />Génération PDF...</>
            ) : (
              <><Icon name="ArrowDownTrayIcon" size={16} />Exporter PDF</>
            )}
          </button>
        </div>

        {/* Source Legend */}
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs font-700 text-foreground mb-3">Légende des sources</p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(sourceConfig) as SourceTag[]).map(key => (
              <SourceBadge key={key} source={key} />
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Une donnée auto-déclarée n'est jamais présentée comme officiellement vérifiée. Seules les données portant le badge "ISX Vérifié" ont été confirmées par le système ISX.</p>
        </div>

        {/* Passport Sections */}
        <div className="space-y-6">
          {passportSections.map(section => (
            <div key={section.title} className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-border bg-muted/30">
                <h2 className="text-sm font-700 text-foreground">{section.title}</h2>
              </div>
              <div className="divide-y divide-border/50">
                {section.fields.map(field => (
                  <div key={field.label} className="flex items-center justify-between px-5 py-3 hover:bg-muted/20 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">{field.label}</p>
                      <p className="text-sm font-600 text-foreground mt-0.5">{field.value}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      {field.verifiedDate && (
                        <span className="text-[10px] text-muted-foreground hidden sm:block">{field.verifiedDate}</span>
                      )}
                      <SourceBadge source={field.source} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Passport Footer */}
        <div className="bg-muted/30 border border-border rounded-xl p-5 space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="DocumentTextIcon" size={16} className="text-muted-foreground" />
            <p className="text-sm font-700 text-foreground">Limites et avertissements</p>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">Ce passeport professionnel ISX est un document de référence analytique. Il ne constitue pas un document officiel gouvernemental, une certification légale, une attestation fiscale, ni un titre professionnel reconnu par une autorité publique. Les données marquées "Auto-déclaré" n'ont pas été vérifiées indépendamment par ISX. Les scores sont calculés selon la méthodologie ISX v1.2 et peuvent évoluer. Date de vérification : 19 Août 2026. © 2026 ISX — Institut Reversal Group.</p>
        </div>
      </div>
    </AppLayout>
  );
}
